import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppConfigService } from '@core/app-config';
import { IncomingFlightDetailsService } from '@core/incoming-flight-details';
import { TextContentService } from '@core/text-content/text-content.service';
import { JbChevronIconSizeEnum } from 'jb-component-library';

import { FlightTrackerResultsService } from '../../flight-tracker-results.service';

@Component({
  selector: 'jb-flight-tracker-results-inbound-plane',
  templateUrl: './flight-tracker-results-inbound-plane.component.html',
  styleUrls: ['./flight-tracker-results-inbound-plane.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightTrackerResultsInboundPlaneComponent implements OnInit {
  @Input('flightTrackerLeg') flightTrackerLeg;
  @Input('flightTrackerText') flightTrackerText;
  @Input('useDialogDisplay') useDialogDisplay = false;

  ariaLiveMessage = '';
  errorMessage = '';
  isExpanded = false;
  isLoading = false;
  indicatorSize = JbChevronIconSizeEnum.small;
  useIncomingFlightsFeature = false;

  flightStatus;
  inboundFlightCopy;

  private _destroy = inject(DestroyRef);

  constructor(
    private flightDetailsService: IncomingFlightDetailsService,
    private flightTrackerResultsService: FlightTrackerResultsService,
    private textContentService: TextContentService,
    private changeDetectorRef: ChangeDetectorRef,
    public appConfig: AppConfigService,
  ) {}

  showInboundPlaneButton() {
    const legFlightStatus = this.flightTrackerLeg?.flightStatus;
    return legFlightStatus === 'ON TIME' || legFlightStatus === 'DELAYED';
  }

  ngOnInit() {
    if (!this.flightTrackerText) {
      this.textContentService
        .getJsonAsset('flight-tracker')
        .pipe(takeUntilDestroyed(this._destroy))
        .subscribe(copy => {
          this.inboundFlightCopy = copy.inboundFlight;
          this.changeDetectorRef.detectChanges();
        });
    } else {
      this.inboundFlightCopy = this.flightTrackerText.inboundFlight;
    }

    this.useIncomingFlightsFeature =
      Boolean(this.appConfig.incomingFlightDetailsUrl) &&
      this.appConfig.isIncomingFlightDetailsEnabled &&
      this.showInboundPlaneButton();
  }

  onApiCallComplete() {
    this.isLoading = false;
    this.changeDetectorRef.detectChanges();
  }

  getIncomingFlightDetails(legDetails) {
    this.flightStatus = undefined;
    this.toggleLoadingUI();
    this.toggleExpandableContainer();

    if (this.isExpanded) {
      this.flightDetailsService
        .getIncomingFlightDetails({
          incomingFlightDetails: {
            carrierCode: 'B6',
            currentFlightNumber: legDetails?.flightNo,
            flightDate: legDetails?.scheduledDeparture?.substring(0, 10),
            tailNumber: legDetails?.tailNumber,
          },
        })
        .subscribe({
          next: response => {
            this.errorMessage = '';
            this.flightStatus = response.body?.['flights']?.[0]?.legs[0];
            this.ariaLiveMessage = this.inboundFlightCopy?.loading?.complete;
            this.onApiCallComplete();
          },
          error: response => {
            this.flightStatus = null;
            const errorMsg = response?.['error'];
            this.ariaLiveMessage = this.inboundFlightCopy?.loading?.failed;

            if (errorMsg === 'Bad Request' || errorMsg === 'Not Found') {
              this.errorMessage = this.inboundFlightCopy.errors.noDataError;
            } else {
              this.errorMessage = this.inboundFlightCopy.errors.internalError;
            }

            this.onApiCallComplete();
          },
        });
    }
  }

  toggleExpandableContainer() {
    this.isExpanded = !this.isExpanded;
  }

  toggleLoadingUI() {
    this.isLoading = !this.flightStatus && !this.isExpanded ? true : false;

    if (this.isLoading) {
      this.ariaLiveMessage = this.inboundFlightCopy?.loading?.inProgress;
    }
  }

  convertTime(time: string) {
    return this.flightTrackerResultsService.convertTime(time);
  }
}
